import { useCallback, useRef, useState } from 'react';

import { JobInteraction, StatusOption, useJobStatusSubmit } from './useJobStatusSubmit';
import { ServiceNodeAllocationData, ServiceUpdateRequest } from '../../@types/service.type';
import {
  updateServiceNodeAllocation,
  updateServiceById,
  createServiceLogsForService,
  getServiceById,
  preservedServiceFields,
} from '../../api/services';
import { BulkRunFailure, BulkRunProgress, bulkRunErrorMessage, runBulk } from '../../utils/bulkRun';

export type { JobInteraction, StatusOption } from './useJobStatusSubmit';

export type DialogType = 'status' | 'assign' | 'schedule' | 'note' | 'bulkEdit' | null;

export interface ListJobActionsReturn {
  activeDialog: DialogType;
  targetIds: number[];
  openRowAction: (serviceId: number, action: string) => void;
  openBulkAction: (action: string, ids: number[]) => void;
  closeDialog: () => void;
  statusOptions: StatusOption[];
  statusLoading: boolean;
  submitStatus: (statusCode: string, notes: string) => Promise<void>;
  submitAllocation: (nodeId: number, allocationTypeCode: string) => Promise<void>;
  submitSchedule: (date: string) => Promise<void>;
  submitNote: (note: string) => Promise<void>;
  submitting: boolean;
  lastError: string | null;
  /** True when the open dialog was launched from the bulk actions bar. */
  isBulkRun: boolean;
  /** How far through the current bulk run we are. Null when idle. */
  progress: BulkRunProgress | null;
  /** Jobs the run could not update, so a retry can be targeted. */
  failures: BulkRunFailure[];
  /** Jobs that need interaction forms before their status can change. */
  pendingInteractions: JobInteraction[];
  /** Called after the caller has submitted interaction forms for all pending jobs. */
  clearPendingInteractions: () => void;
  /** Cancel interactions and go back to the status selection dialog. */
  cancelPendingInteractions: () => void;
}

const ACTION_MAP: Record<string, DialogType> = {
  update_status: 'status',
  bulk_update: 'bulkEdit',
  assign_tech: 'assign',
  schedule: 'schedule',
  add_note: 'note',
};

export const useListJobActions = (onRefresh?: () => void): ListJobActionsReturn => {
  const [activeDialog, setActiveDialog] = useState<DialogType>(null);
  const [targetIds, setTargetIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [progress, setProgress] = useState<BulkRunProgress | null>(null);
  // Whether the open dialog came from the bulk actions bar rather than a row's
  // menu. A bulk status change bypasses interaction forms; a row's does not.
  const [isBulkRun, setIsBulkRun] = useState(false);
  const [failures, setFailures] = useState<BulkRunFailure[]>([]);
  const status = useJobStatusSubmit();
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  const { fetchStatusOptions, resetStatusOptions, clearPendingInteractions: clearPending } = status;

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setTargetIds([]);
    setLastError(null);
    setProgress(null);
    setFailures([]);
    resetStatusOptions();
  }, [resetStatusOptions]);

  const openDialog = useCallback(
    (dialog: DialogType, ids: number[], bulk: boolean) => {
      setLastError(null);
      setProgress(null);
      setFailures([]);
      resetStatusOptions();
      setIsBulkRun(bulk);
      setTargetIds(ids);
      setActiveDialog(dialog);
      if (dialog === 'status') fetchStatusOptions(ids);
    },
    [fetchStatusOptions, resetStatusOptions]
  );

  const openRowAction = useCallback(
    (serviceId: number, action: string) => {
      const dialog = ACTION_MAP[action] ?? null;
      if (!dialog) return;
      openDialog(dialog, [serviceId], false);
    },
    [openDialog]
  );

  const openBulkAction = useCallback(
    (action: string, ids: number[]) => {
      const dialog = ACTION_MAP[action] ?? null;
      if (!dialog) return;
      openDialog(dialog, ids, true);
    },
    [openDialog]
  );

  /**
   * Applies an action to every selected job, reporting progress as it goes.
   *
   * A failure no longer stops the run: on 100 jobs, aborting at the third left
   * the other 97 unattempted with nothing on screen to say so. Every job is
   * attempted and the failures are named, so a retry can be targeted.
   */
  const runForAll = useCallback(
    async (fn: (id: number) => Promise<unknown>, errorMsg: string) => {
      setSubmitting(true);
      setLastError(null);
      setFailures([]);
      setProgress({ completed: 0, total: targetIds.length });
      try {
        const result = await runBulk(targetIds, fn, { onProgress: setProgress });
        refreshRef.current?.();
        if (result.failed.length > 0) {
          setFailures(result.failed);
          setLastError(bulkRunErrorMessage(result));
          return;
        }
        closeDialog();
      } catch (err) {
        setLastError(err instanceof Error ? err.message : errorMsg);
      } finally {
        setSubmitting(false);
      }
    },
    [targetIds, closeDialog]
  );

  const submitJobStatus = status.submitStatus;
  const submitStatus = useCallback(
    async (statusCode: string, notes: string) => {
      setSubmitting(true);
      setLastError(null);
      setFailures([]);
      setProgress({ completed: 0, total: targetIds.length });
      try {
        // Only a fully committed run closes the dialog; a pending-interactions
        // outcome leaves it open so the user can fill the forms or cancel.
        const outcome = await submitJobStatus(targetIds, statusCode, notes, setProgress, isBulkRun);
        if (outcome.state === 'pending-interactions') {
          // Nothing was committed, so a progress bar left at its last count
          // would sit behind the interaction dialog implying work happened.
          setProgress(null);
          return;
        }
        refreshRef.current?.();
        if (outcome.failed.length > 0) {
          setFailures(outcome.failed);
          setLastError(
            bulkRunErrorMessage({ succeeded: outcome.succeeded, failed: outcome.failed })
          );
          return;
        }
        closeDialog();
      } catch (err) {
        setLastError(err instanceof Error ? err.message : 'Status update failed');
      } finally {
        setSubmitting(false);
      }
    },
    [targetIds, closeDialog, submitJobStatus, isBulkRun]
  );

  const clearPendingInteractions = useCallback(() => {
    clearPending();
    refreshRef.current?.();
    closeDialog();
  }, [clearPending, closeDialog]);

  const cancelPendingInteractions = useCallback(() => {
    clearPending();
    closeDialog();
  }, [clearPending, closeDialog]);

  const submitAllocation = useCallback(
    async (nodeId: number, typeCode: string) => {
      await runForAll((id) => {
        const data: ServiceNodeAllocationData = {
          serviceId: id,
          userDepotAllocationNodeId: nodeId,
          allocationTypeCode: typeCode,
        };
        return updateServiceNodeAllocation(data);
      }, 'Allocation update failed');
    },
    [runForAll]
  );

  const submitSchedule = useCallback(
    async (date: string) => {
      await runForAll(async (id) => {
        const svc = await getServiceById(id);
        const payload = {
          id,
          serviceTypeCode: svc.serviceTypeCode ?? '',
          serviceStatusCode: svc.serviceStatusCode ?? svc.serviceStatus?.code ?? '',
          actionTypeCode: svc.actionTypeCode ?? '',
          processTypeCode: svc.processTypeCode ?? '',
          nodeId: svc.mainLocationNodeId ?? 0,
          priorityCode: String(svc.priorityCode ?? ''),
          // Preserve the follow-up date and sub-location the full PUT would
          // otherwise wipe; scheduledAt below overrides the echoed value.
          ...preservedServiceFields(svc),
          scheduledAt: date,
        };
        await updateServiceById(id, payload as ServiceUpdateRequest);
      }, 'Schedule update failed');
    },
    [runForAll]
  );

  const submitNote = useCallback(
    async (note: string) => {
      await runForAll(
        (id) => createServiceLogsForService(id, { notes: note }),
        'Failed to add note'
      );
    },
    [runForAll]
  );

  return {
    activeDialog,
    targetIds,
    openRowAction,
    openBulkAction,
    closeDialog,
    statusOptions: status.statusOptions,
    statusLoading: status.statusLoading,
    submitStatus,
    submitAllocation,
    submitSchedule,
    submitNote,
    submitting,
    lastError,
    isBulkRun,
    progress,
    failures,
    pendingInteractions: status.pendingInteractions,
    clearPendingInteractions,
    cancelPendingInteractions,
  };
};
